import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { Typeahead } from "react-bootstrap-typeahead";
import { Form } from "reactstrap";

import "react-bootstrap-typeahead/css/Typeahead.css";
import "react-bootstrap-typeahead/css/Typeahead.bs5.css";
import "./style.scss";
import MUIIcons from "../MUIIcon/MUIIcons";

export interface MultiSelectDropdownDataItem {
  name: string;
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectDropdownDataItem[];
  placeholder: string;
  onChange: (selectedIds: string[]) => void;
  defaultSelected?: MultiSelectDropdownDataItem[];
  disabled?: boolean;
  onBlurDropdown?: (value: any) => void;
  closeButton?: boolean;
  tabIndex?: number;
  enableSelectDeselectAll?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  placeholder,
  onChange,
  defaultSelected = [],
  disabled = false,
  onBlurDropdown,
  closeButton = true,
  tabIndex,
  enableSelectDeselectAll = false,
}) => {
  const SELECT_ALL = {
    name: "__select_all__",
    value: "__select_all__",
    label: "Select All",
  };
  const DESELECT_ALL = {
    name: "__deselect_all__",
    value: "__deselect_all__",
    label: "Deselect All",
  };

  const [selectedItems, setSelectedItems] =
    useState<MultiSelectDropdownDataItem[]>(defaultSelected);
  const [isOpen, setIsOpen] = useState(false);

  const isAllSelected =
    selectedItems?.length === options?.length &&
    options?.every(opt => selectedItems?.some(sel => sel?.name === opt?.name));

  const enhancedOptions = useMemo(() => {
    const sortedOptions = (options ?? [])?.sort((labelOne, labelTwo) =>
      labelOne?.label?.localeCompare(labelTwo?.label)
    );

    if (!enableSelectDeselectAll) return sortedOptions;

    if (isAllSelected) return [DESELECT_ALL, ...sortedOptions];

    return [SELECT_ALL, ...sortedOptions];
  }, [options, enableSelectDeselectAll, selectedItems]);

  useEffect(() => {
    setSelectedItems(defaultSelected);
  }, [defaultSelected]);

  const handleToggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  const handleCloseDropdown = () => {
    if (onBlurDropdown) onBlurDropdown(selectedItems);
    setIsOpen(false);
  };

  const handleChange = (selected: MultiSelectDropdownDataItem[]) => {
    if (selected.some(item => item.name === SELECT_ALL.name)) {
      setSelectedItems(options);
      onChange(options.map(item => item.name));
      return;
    }

    if (selected.some(item => item.name === DESELECT_ALL.name)) {
      setSelectedItems([]);
      onChange([]);
      return;
    }

    const filtered = selected.filter(
      item => item.name !== SELECT_ALL.name && item.name !== DESELECT_ALL.name
    );

    setSelectedItems(filtered);
    onChange(filtered.map(item => item.name));
  };

  return (
    <div id="multiple-datasets">
      <Form className="theme-form">
        <div tabIndex={tabIndex}>
          <Typeahead
            disabled={disabled}
            options={enhancedOptions}
            multiple
            clearButton={closeButton}
            placeholder={placeholder}
            labelKey="label"
            selected={selectedItems}
            id="1"
            open={isOpen}
            onFocus={handleToggleDropdown}
            onBlur={handleCloseDropdown}
            onChange={handleChange}
            defaultSelected={defaultSelected}
          />
          {selectedItems?.length === 0 && !disabled && (
            <span
              className="multiple-datasets__down-arrow"
              onClick={handleToggleDropdown}
            >
              <MUIIcons className="" size={20} iconName="ExpandMoreOutlined" />
            </span>
          )}
        </div>
      </Form>
    </div>
  );
};

MultiSelectDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired as PropTypes.Validator<MultiSelectDropdownDataItem[]>,
  placeholder: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  defaultSelected: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ) as PropTypes.Validator<MultiSelectDropdownDataItem[]>,
  enableSelectDeselectAll: PropTypes.bool,
};

export default MultiSelectDropdown;
